import { useEffect, useState, type ReactNode } from 'react';
import { usePrefs } from '../prefs.ts';
import { useSession } from '../session.tsx';
import { BACKGROUNDS, getTheme, isAccentId, resolveTokens, type Mode } from './themes.ts';

function useSystemDark(): boolean {
  const query = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined;
  const [dark, setDark] = useState(query?.matches ?? false);
  useEffect(() => {
    if (!query) return;
    const onChange = () => setDark(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [query]);
  return dark;
}

/**
 * Applies the active theme as CSS custom properties on <html>. Resolution:
 * this device's choice → the family's default → built-in default.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const prefs = usePrefs().appearance;
  const family = useSession().activeFamily?.settings;
  const systemDark = useSystemDark();

  const mode: Mode = prefs.mode === 'system' ? (systemDark ? 'dark' : 'light') : prefs.mode;
  const theme = getTheme(prefs.theme ?? family?.theme);
  const accentPref = prefs.accent ?? family?.accent;
  const accent = isAccentId(accentPref) ? accentPref : theme.defaultAccent;
  const backgroundPref = prefs.background ?? family?.background ?? 'plain';
  const background = BACKGROUNDS.some((b) => b.id === backgroundPref) ? backgroundPref : 'plain';

  useEffect(() => {
    const root = document.documentElement;
    for (const [k, v] of Object.entries(resolveTokens(theme, mode, accent))) root.style.setProperty(k, v);
    root.style.colorScheme = mode;
    root.dataset.theme = theme.id;
    root.dataset.mode = mode;
    root.dataset.background = background;
    root.dataset.textSize = prefs.textSize;
    if (prefs.reduceMotion === 'system') delete root.dataset.reduceMotion;
    else root.dataset.reduceMotion = prefs.reduceMotion === 'reduce' ? 'true' : 'false';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme[mode].bg);
  }, [theme, mode, accent, background, prefs.textSize, prefs.reduceMotion]);

  return (
    <>
      <div className="app-background" aria-hidden="true" />
      {children}
    </>
  );
}
